<template>
  <div
    v-if="showUpgradeButton"
    class="flex items-center justify-center overflow-hidden transition-[height,width] duration-150 ease-out"
    :class="{
      'h-[74px] min-h-[74px] w-[277px]': !collapsed,
      'size-[37px]': collapsed,
    }"
  >
    <button
      v-tooltip="collapsed ? upgradeText : ''"
      class="
        flex items-center justify-center overflow-hidden border
        border-[--color-brand-tangerine] bg-[--color-brand-tangerine] text-sm font-medium
        text-white transition-[background-color,border-color,border-radius,height,width] duration-150 ease-out
        hover:border-[--color-brand-tangerine-hover] hover:bg-[--color-brand-tangerine-hover]
        active:border-[--color-brand-tangerine-dark] active:bg-[--color-brand-tangerine-dark]
      "
      :class="{
        'min-h-[42px] w-[245px] gap-[8px] rounded-lg px-4 py-2': !collapsed,
        'size-[37px] rounded-full': collapsed,
      }"
      :aria-label="upgradeText"
      @click="handleClick"
    >
      <Icon
        name="arrow-up"
        class="shrink-0 text-white transition-[height,width] duration-150 ease-out"
        :class="collapsed ? 'size-[18px]' : 'size-[16px]'"
      />
      <span
        class="whitespace-nowrap font-medium leading-none transition-[margin,max-width,opacity] duration-150 ease-out"
        :class="collapsed ? 'ml-0 max-w-0 opacity-0' : 'ml-[8px] max-w-[160px] opacity-100'"
      >
        {{ label !== '' ? label : upgradeText }}
      </span>
    </button>
  </div>
  <div v-else class="hidden" />
</template>

<script setup lang='ts'>
import { useConfirm } from 'primevue/useconfirm';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Icon from '@/common/components/atoms/Icon';
import { useAuthStore } from '@/common/stores/auth';
import { track } from '@/helpers/analytics/mixpanel';
import { usePaddleStore } from '@/modules/pricing/stores/paddle';
import type { Plan } from '@/types/global';

const props = withDefaults(defineProps<{
  collapsed?: boolean;
  plan: Plan;
  label?: string;
  source?: string;
  showBtnAlways?: boolean;
}>(), {
  collapsed: false,
  label: '',
  source: '',
  showBtnAlways: false,
});


const router = useRouter();
const confirm = useConfirm();

const { t } = useI18n();
const authStore = useAuthStore();
const paddleStore = usePaddleStore();

const flag = ref(false);
const isGifted = computed(() => authStore.user?.is_gifted === true);
const showUpgradeButton = computed(() => (props.plan != 'expert' && !isGifted.value) || props.showBtnAlways);
const upgradeText = computed(() => {
  return t('layout.upgrade_plan');
});

const handleClick = () => {
  track('Clicked Upgrade', { Source: props.source != '' ? props.source : 'Sidebar' });
  if(flag.value == true) {
    confirm.require({
      group: 'headless',
      header: 'Your region is not supported ',
      message: `
        <p class='v3-body-regular-400-line-height-auto'>
          We’re sorry, but we are unable to accept your payment due to international sanctions regulations impacting your region.</p>
          <br><br>
          You may continue to use eRank as a Free member.
        </p>
      `,
      accept: () => confirm.close,
      acceptLabel: 'Close',
      rejectClass: 'hidden',
    });
  } else {
    const referrer = authStore.user.referrer;
    let redirect = '';
    switch (referrer) {
      case 'ad':
      case 'ad-sp':
        redirect = '/plans';
        break;
      case 'ad-reg':
        // Users from ad-reg should go directly to plans page
        redirect = '/plans';
        break;
      default:
        redirect = `/a/${referrer}`;
    }
    router.push(authStore.redirectToReferralPage && referrer && referrer !== 'ad-reg' ? redirect : '/plans');
  }
};

onMounted(async () => {
  try {
    if(authStore.isUserLogged) {
      flag.value = await paddleStore.checkPaddleRestriction();
    }
  } catch(e) { /* empty */ }
});
</script>
